import { useState, useRef } from 'react';
import parse from 'html-react-parser';
import { SuggestionContentResponseModel } from '@/services/platform/api/model';
import { cn } from '@/lib/core/utils/cn';
import { SuggestionMiniCard } from './miniCard';

// ── Suggestions ───────────────────────────────────────────────────────────────

interface SuggestionsProps {
  items: SuggestionContentResponseModel[];
  titleHtml?: string;
  onItemClick: (item: SuggestionContentResponseModel) => void;
}

export function Suggestions({ items, titleHtml, onItemClick }: SuggestionsProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  if (!items.length) return null;

  const onScroll = () => {
    if (!scrollRef.current) return;
    const cardWidth = 244 + 8;
    setActiveIndex(Math.round(scrollRef.current.scrollLeft / cardWidth));
  };

  return (
    <div className="flex flex-col gap-6 pb-2">
      {titleHtml && <div className="book-intro-suggestions-title">{parse(titleHtml)}</div>}

      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="scrollbar-none flex w-[calc(100%+16px)] snap-x snap-mandatory gap-2 overflow-x-auto"
        style={{ WebkitOverflowScrolling: 'touch' }}
        aria-label="Suggestions"
      >
        {items.map((item, i) => (
          <SuggestionMiniCard key={item.id ?? i} item={item} onClick={() => onItemClick(item)} />
        ))}
        <div className="w-4 shrink-0" aria-hidden="true" />
      </div>

      {items.length > 1 && (
        <div className="flex items-center justify-center gap-2" aria-hidden="true">
          {items.map((_, i) => (
            <span
              key={i}
              className={cn(
                'block h-px transition-all duration-300',
                i === activeIndex ? 'w-4 bg-[#1a1a1a]' : 'w-4 bg-[#d1d1d1]'
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
}
